import * as React from 'react'

/**
 * How far the page must move before the header darkens. A few pixels rather than
 * zero: TV browsers and trackpads report sub-pixel scroll offsets at rest, and a
 * threshold of 0 makes the gradient flicker on and off while the page is still.
 */
const SCROLLED_THRESHOLD_PX = 8

/**
 * Marks the header `data-scrolled` once the page has moved off the top, which is
 * what its background is keyed to (see `.Header[data-scrolled]` in style.scss).
 *
 * At rest the header is transparent — a page that opens on ordinary content has
 * nothing behind the chrome, and a gradient there is just a black band. Only a
 * full-bleed banner sits under it at rest, and that scrims its own top
 * (`.mlsg-hero::before`). Once anything scrolls beneath the header, though, the
 * chrome has to separate itself from it, whatever page it is.
 *
 * **Writes the attribute directly rather than holding React state.** Scroll fires
 * continuously; a `useState` here would re-render the whole shell subtree — header,
 * page, every pilet — on every frame of every scroll. The DOM node is the only thing
 * that needs to change, so it is the only thing that does.
 *
 * Reads are rAF-coalesced (many scroll events, at most one style write per frame)
 * and the listener is passive, so it can never delay the scroll itself.
 *
 * Returns a **ref callback**, not a ref object, and that is load-bearing: the header
 * is not rendered while the startup gate is holding up the loading screen, so an
 * effect reading `ref.current` on mount would find null and — the ref object being
 * stable — never run again once the header finally appeared. React calls this
 * callback with the node at the moment it mounts, which is exactly when the listener
 * should be attached.
 */
export function useHeaderScrim (): (node: HTMLElement | null) => void {
  const [node, setNode] = React.useState<HTMLElement | null>(null)

  React.useEffect(() => {
    if (!node) return
    let frame = 0

    const apply = () => {
      frame = 0
      node.setAttribute('data-scrolled', window.scrollY > SCROLLED_THRESHOLD_PX ? 'true' : 'false')
    }
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(apply)
    }

    // Set the initial state before any scroll happens — a deep link restored partway
    // down the page must not start with a transparent header over its own content.
    apply()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [node])

  return setNode
}
